import { Component, For, Show, createMemo, createSignal } from "solid-js"
import { getSegmentEffectiveEndTime, getSegmentEffectiveStartTime } from "../lib/geometry"
import { useAppStore } from "../stores/app"
import { useScenarioStore } from "../stores/scenario"
import type { Agent, AgentType, Segment } from "../types"
import { AGENT_EMOJIS, AGENT_TYPE_LABELS, PERSON_SVG_OPTIONS, formatSimTime, formatSpeed } from "../types"

// Import SVG icons
import coupleSvg from "../assets/agents/person/couple-mw.svg?raw"
import femaleSvg from "../assets/agents/person/female.svg?raw"
import maleSvg from "../assets/agents/person/male.svg?raw"

// SVG lookup map
const SVG_MAP: Record<string, string> = {
    "male.svg": maleSvg,
    "female.svg": femaleSvg,
    "couple-mw.svg": coupleSvg,
}

interface AgentPanelProps {}

const AgentPanel: Component<AgentPanelProps> = (_props) => {
    const [scenario, scenarioActions] = useScenarioStore();
    const [app, appActions] = useAppStore();
    
    const sortByTypeAndName = (list: Agent[]) => {
        return list.slice().sort((a, b) => {
            // Sort by type first
            if (a.type !== b.type) {
                const typeOrder = {
                    person: 0,
                    car: 1,
                    police: 2,
                    ambulance: 3,
                }
                return (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99)
            }
            // Then sort alphabetically by name within the same type
            const nameA = (a.name || a.label || "").toLowerCase()
            const nameB = (b.name || b.label || "").toLowerCase()
            return nameA.localeCompare(nameB)
        })
    }

    const sortedAgents = createMemo<Agent[]>(() => {
        const sorted = sortByTypeAndName(scenario.agents)
        const selectedId = app.selectedAgentId

        if (!selectedId) {
            return sorted
        }

        const selectedIndex = sorted.findIndex((agent) => agent.id === selectedId)
        if (selectedIndex <= 0) {
            return sorted
        }

        const selectedAgent = sorted[selectedIndex]
        return [selectedAgent, ...sorted.slice(0, selectedIndex), ...sorted.slice(selectedIndex + 1)]
    })

    const handleAddAgent = (type: AgentType) => {
        scenarioActions.addAgent(type)
        // Get the newly added agent (last one with this type)
        const newAgent = scenario.agents[scenario.agents.length - 1]
        if (newAgent) appActions.selectAgent(newAgent.id)
        scenarioActions.scheduleSave()
    }

    const handleRemove = (id: string) => {
        scenarioActions.removeAgent(id)
        if (app.selectedAgentId === id) appActions.selectAgent(null)
        scenarioActions.scheduleSave()
    }

    const handleTimeChange = (
        agentId: string,
        segIdx: number,
        which: "start" | "end",
        field: "hour" | "minute" | "second",
        value: string
    ) => {
        const agent = scenario.agents.find((a) => a.id === agentId)
        if (!agent) return
        const seg = agent.segments[segIdx]
        if (!seg) return
        const num = parseInt(value, 10)
        if (isNaN(num)) return
        const timeObj = which === "start" ? seg.startTime : seg.endTime
        const updated = { ...timeObj, [field]: num }
        if (which === "start") scenarioActions.setSegmentStartTime(agentId, segIdx, updated)
        else scenarioActions.setSegmentEndTime(agentId, segIdx, updated)
        scenarioActions.scheduleSave()
    }

    const TimeInputs = (p: {
        agentId: string
        segIdx: number
        which: "start" | "end"
        time: { hour: number; minute: number; second: number }
    }) => (
        <div class="flex gap-1">
            <input
                type="number"
                min="0"
                max="23"
                value={p.time.hour}
                onInput={(e) => handleTimeChange(p.agentId, p.segIdx, p.which, "hour", e.currentTarget.value)}
                class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
            />
            <span class="self-center text-xs text-gray-400">:</span>
            <input
                type="number"
                min="0"
                max="59"
                value={p.time.minute}
                onInput={(e) => handleTimeChange(p.agentId, p.segIdx, p.which, "minute", e.currentTarget.value)}
                class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
            />
            <span class="self-center text-xs text-gray-400">:</span>
            <input
                type="number"
                min="0"
                max="59"
                value={p.time.second}
                onInput={(e) => handleTimeChange(p.agentId, p.segIdx, p.which, "second", e.currentTarget.value)}
                class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
            />
        </div>
    )

    const SegmentEditor = (p: { agent: Agent; seg: Segment; segIdx: number }) => {
        const isActive = () => app.activeSegmentIdx === p.segIdx
        return (
            <div
                class={`space-y-1.5 rounded border p-2 ${isActive() ? "border-blue-400 bg-blue-50/50" : "border-gray-200"}`}
            >
                <div class="flex items-center justify-between">
                    <button
                        class="text-xs font-semibold text-gray-600"
                        onClick={() => appActions.setActiveSegmentIdx(p.segIdx)}
                    >
                        Segment {p.segIdx + 1}
                    </button>
                    <Show when={p.agent.segments.length > 1}>
                        <button
                            class="text-xs text-red-400 hover:text-red-600"
                            onClick={() => {
                                scenarioActions.removeSegment(p.agent.id, p.segIdx)
                                scenarioActions.scheduleSave()
                            }}
                        >
                            ×
                        </button>
                    </Show>
                </div>

                <select
                    value={p.seg.timeMode}
                    onChange={(e) => {
                        scenarioActions.setSegmentTimeMode(p.agent.id, p.segIdx, e.currentTarget.value as any)
                        scenarioActions.scheduleSave()
                    }}
                    class="w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-xs"
                >
                    <option value="start">Ange starttid</option>
                    <option value="end">Ange sluttid</option>
                </select>

                <div>
                    <label class="text-[10px] text-gray-500">
                        Start{" "}
                        {p.seg.timeMode === "end" && p.seg.waypoints.length >= 2 ? (
                            <span class="text-blue-500 italic">
                                ({formatSimTime(getSegmentEffectiveStartTime(p.seg, p.agent.speed))})
                            </span>
                        ) : null}
                    </label>
                    <Show when={p.seg.timeMode === "start"}>
                        <TimeInputs agentId={p.agent.id} segIdx={p.segIdx} which="start" time={p.seg.startTime} />
                    </Show>
                </div>

                <div>
                    <label class="text-[10px] text-gray-500">
                        Slut{" "}
                        {p.seg.timeMode === "start" && p.seg.waypoints.length >= 2 ? (
                            <span class="text-blue-500 italic">
                                ({formatSimTime(getSegmentEffectiveEndTime(p.seg, p.agent.speed))})
                            </span>
                        ) : null}
                    </label>
                    <Show when={p.seg.timeMode === "end"}>
                        <TimeInputs agentId={p.agent.id} segIdx={p.segIdx} which="end" time={p.seg.endTime} />
                    </Show>
                </div>

                {/* Add waypoints button - activates this segment and enables map clicks */}
                <button
                    class={`w-full rounded px-2 py-1 text-xs font-medium transition-colors ${
                        isActive() && app.isAddingWaypoints
                            ? "bg-green-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                    onClick={() => {
                        appActions.setActiveSegmentIdx(p.segIdx)
                        if (!isActive()) {
                            // Just switched to this segment; don't toggle yet
                        } else {
                            appActions.toggleIsAddingWaypoints()
                        }
                    }}
                >
                    {isActive() && app.isAddingWaypoints
                        ? "Lägger till vägpunkter... (klicka på kartan)"
                        : "Lägg till vägpunkter"}
                </button>

                <Show when={p.seg.waypoints.length > 0}>
                    <div class="max-h-32 space-y-0.5 overflow-y-auto pr-2">
                        <For each={p.seg.waypoints}>
                            {(wp, idx) => (
                                <div class="rounded border border-gray-100 bg-white px-1.5 py-0.5 text-[10px]">
                                    <div class="flex items-center justify-between">
                                        <span class="font-mono text-gray-500">
                                            #{idx() + 1} ({wp.position[0].toFixed(4)}, {wp.position[1].toFixed(4)})
                                        </span>
                                        <button
                                            class="text-red-400 hover:text-red-600"
                                            onClick={() => {
                                                scenarioActions.removeWaypoint(p.agent.id, p.segIdx, idx())
                                                scenarioActions.scheduleSave()
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <div class="flex items-center gap-1">
                                        <label class="text-gray-400">Stopp:</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={wp.stopDuration}
                                            onInput={(e) => {
                                                scenarioActions.setWaypointStopDuration(
                                                    p.agent.id,
                                                    p.segIdx,
                                                    idx(),
                                                    parseInt(e.currentTarget.value) || 0
                                                )
                                                scenarioActions.scheduleSave()
                                            }}
                                            class="w-12 rounded border border-gray-200 px-1 py-0 text-center"
                                        />
                                        <span class="text-gray-400">s</span>
                                    </div>
                                    <div class="flex items-center gap-1">
                                        <label class="text-gray-400">Hastighet:</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0.1"
                                            value={wp.speed ?? ""}
                                            placeholder={p.agent.speed.toFixed(1)}
                                            onInput={(e) => {
                                                const val = e.currentTarget.value
                                                const speed = val ? parseFloat(val) : undefined
                                                scenarioActions.setWaypointSpeed(p.agent.id, p.segIdx, idx(), speed)
                                                scenarioActions.scheduleSave()
                                            }}
                                            class="w-12 rounded border border-gray-200 px-1 py-0 text-center"
                                        />
                                        <span class="text-gray-400">m/s</span>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </div>
        )
    }

    return (
        <>
            <div class="border-b border-gray-200 bg-white p-3">
                <h2 class="mb-2 text-sm font-semibold text-gray-700">Aktörer</h2>
                <p class="mb-2 text-[10px] text-gray-400">
                    Lägg till och hantera aktörer i scenariot. Klicka på en aktör för att redigera dess rutt.
                </p>
                <Show when={app.mode === "edit"}>
                    <div class="flex flex-wrap gap-1">
                        <For each={["person", "car", "police", "ambulance"] as AgentType[]}>
                            {(type) => (
                                <button
                                    class="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-300"
                                    onClick={() => handleAddAgent(type)}
                                >
                                    + {AGENT_EMOJIS[type]} {AGENT_TYPE_LABELS[type]}
                                </button>
                            )}
                        </For>
                    </div>
                </Show>
            </div>

            <div class="flex-1 overflow-y-auto">
                <For each={sortedAgents()}>
                    {(agent) => {
                        const isSelected = () => app.selectedAgentId === agent.id

                        return (
                            <div
                                class={`cursor-pointer border-b border-gray-200 ${isSelected() ? "border-l-2 border-l-blue-500 bg-blue-50" : "hover:bg-gray-100"}`}
                                onClick={() => appActions.selectAgent(isSelected() ? null : agent.id)}
                            >
                                <div class="flex items-center gap-2 px-3 py-2">
                                    <div
                                        class="h-3 w-3 flex-shrink-0 rounded-full"
                                        style={{
                                            "background-color":
                                                agent.type === "person"
                                                    ? "#000000"
                                                    : agent.type === "car"
                                                      ? "#de140b"
                                                      : "#113db3",
                                        }}
                                    />
                                    <span class="flex-1 truncate text-sm font-medium">{agent.name || agent.label}</span>
                                    <div
                                        class="flex h-4 w-4 flex-shrink-0 items-center justify-center overflow-hidden"
                                        innerHTML={(() => {
                                            // If agent has svg property, use it; otherwise fallback to emoji
                                            if (agent.svg && SVG_MAP[agent.svg]) {
                                                return `<div class="sim-svg-icon" style="width: 16px; height: 16px; color: #000000;">${SVG_MAP[agent.svg]}</div>`
                                            }
                                            return `<span style="font-size: 14px; line-height: 1;">${agent.emoji}</span>`
                                        })()}
                                    />
                                    <span class="text-xs text-gray-400">
                                        {agent.segments.reduce((n: number, s: Segment) => n + s.waypoints.length, 0)}{" "}
                                        punkter
                                    </span>
                                </div>

                                <Show when={isSelected() && app.mode === "edit"}>
                                    <div class="space-y-2 px-3 pb-3" onClick={(e) => e.stopPropagation()}>
                                        {/* Label + Speed */}
                                        <div class="grid grid-cols-2 gap-2">
                                            <div>
                                                <label class="text-[10px] text-gray-500">Etikett (kort)</label>
                                                <input
                                                    type="text"
                                                    value={agent.label}
                                                    onInput={(e) => {
                                                        scenarioActions.updateAgent(agent.id, {
                                                            label: e.currentTarget.value,
                                                        })
                                                        scenarioActions.scheduleSave()
                                                    }}
                                                    class="w-full rounded border border-gray-300 px-1 py-0.5 text-xs"
                                                />
                                            </div>
                                            <div>
                                                <label class="text-[10px] text-gray-500">
                                                    Hastighet (m/s) - {formatSpeed(agent.speed)}
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    min="0.1"
                                                    value={agent.speed}
                                                    onInput={(e) => {
                                                        const s = parseFloat(e.currentTarget.value)
                                                        if (s > 0) {
                                                            scenarioActions.updateAgent(agent.id, { speed: s })
                                                            scenarioActions.scheduleSave()
                                                        }
                                                    }}
                                                    class="w-full rounded border border-gray-300 px-1 py-0.5 text-xs"
                                                />
                                            </div>
                                        </div>

                                        {/* Namn (visas vid hovring) */}
                                        <div>
                                            <label class="text-[10px] text-gray-500">Namn</label>
                                            <input
                                                type="text"
                                                value={agent.name}
                                                onInput={(e) => {
                                                    scenarioActions.updateAgent(agent.id, {
                                                        name: e.currentTarget.value,
                                                    })
                                                    scenarioActions.scheduleSave()
                                                }}
                                                class="w-full rounded border border-gray-300 px-1 py-0.5 text-xs"
                                                placeholder="t.ex. Lisbeth och Olof Palme"
                                            />
                                        </div>

                                        {/* SVG selector for person agents */}
                                        <Show when={agent.type === "person"}>
                                            <div>
                                                <label class="text-[10px] text-gray-500">Ikon</label>
                                                <select
                                                    value={agent.svg || "male.svg"}
                                                    onChange={(e) => {
                                                        scenarioActions.updateAgent(agent.id, {
                                                            svg: e.currentTarget.value,
                                                        })
                                                        scenarioActions.scheduleSave()
                                                    }}
                                                    class="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                                                >
                                                    <For each={PERSON_SVG_OPTIONS}>
                                                        {(option) => (
                                                            <option value={option.value}>{option.label}</option>
                                                        )}
                                                    </For>
                                                </select>
                                            </div>
                                        </Show>

                                        {/* Emoji for non-person agents */}
                                        <Show when={agent.type !== "person"}>
                                            <div>
                                                <label class="text-[10px] text-gray-500">Emoji</label>
                                                <input
                                                    type="text"
                                                    value={agent.emoji}
                                                    maxLength={4}
                                                    onInput={(e) => {
                                                        scenarioActions.updateAgent(agent.id, {
                                                            emoji: e.currentTarget.value,
                                                        })
                                                        scenarioActions.scheduleSave()
                                                    }}
                                                    class="w-16 rounded border border-gray-300 px-1 py-0.5 text-center text-sm"
                                                />
                                            </div>
                                        </Show>

                                        {/* Track */}
                                        <div>
                                            <label class="text-[10px] text-gray-500">
                                                Händelseförlopp (max 300 tecken)
                                            </label>
                                            <textarea
                                                value={agent.track}
                                                maxLength={300}
                                                onInput={(e) => {
                                                    scenarioActions.updateAgent(agent.id, {
                                                        track: e.currentTarget.value.replace(/[ \t]+$/gm, ""),
                                                    })
                                                    scenarioActions.scheduleSave()
                                                }}
                                                rows={3}
                                                class="w-full resize-none rounded border border-gray-300 px-2 py-1 text-xs"
                                                placeholder="Beskrivning av väg eller annat (valfritt)"
                                            />
                                        </div>

                                        {/* Testimony */}
                                        <div>
                                            <label class="text-[10px] text-gray-500">Vittnesmål (max 500 tecken)</label>
                                            <textarea
                                                value={agent.testimony}
                                                maxLength={500}
                                                onInput={(e) => {
                                                    scenarioActions.updateAgent(agent.id, {
                                                        testimony: e.currentTarget.value.replace(/[ \t]+$/gm, ""),
                                                    })
                                                    scenarioActions.scheduleSave()
                                                }}
                                                class="w-full resize-none rounded border border-gray-300 px-2 py-1 text-xs"
                                                rows={4}
                                                placeholder="Vittnesmål eller signalement..."
                                            />
                                            <div class="text-right text-[9px] text-gray-400">
                                                {agent.testimony.length}/500
                                            </div>
                                        </div>

                                        {/* Remove on end checkbox */}
                                        <div class="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id={`remove-${agent.id}`}
                                                checked={agent.removeOnEnd ?? false}
                                                onChange={(e) => {
                                                    scenarioActions.updateAgent(agent.id, {
                                                        removeOnEnd: e.currentTarget.checked,
                                                    })
                                                    scenarioActions.scheduleSave()
                                                }}
                                                class="h-3 w-3 rounded border-gray-300"
                                            />
                                            <label for={`remove-${agent.id}`} class="text-xs text-gray-600">
                                                Ta bort aktör när vägen slutar
                                            </label>
                                        </div>

                                        {/* Segments */}
                                        <div class="space-y-2">
                                            <For each={agent.segments}>
                                                {(seg, segIdx) => (
                                                    <SegmentEditor agent={agent} seg={seg} segIdx={segIdx()} />
                                                )}
                                            </For>
                                            <button
                                                class="w-full rounded border border-blue-300 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                                                onClick={() => {
                                                    scenarioActions.addSegment(agent.id)
                                                    appActions.setActiveSegmentIdx(agent.segments.length - 1)
                                                    scenarioActions.scheduleSave()
                                                }}
                                            >
                                                + Lägg till segment
                                            </button>
                                        </div>

                                        {/* Sources */}
                                        <div>
                                            <div class="mb-1 flex items-center justify-between">
                                                <label class="text-[10px] text-gray-500">Källor</label>
                                                <button
                                                    class="text-xs text-blue-600 hover:text-blue-700"
                                                    onClick={() => {
                                                        scenarioActions.addSource(agent.id)
                                                        scenarioActions.scheduleSave()
                                                    }}
                                                    title="Lägg till källa"
                                                >
                                                    + Lägg till
                                                </button>
                                            </div>
                                            <Show when={(agent.sources?.length || 0) === 0}>
                                                <div class="text-[10px] text-gray-400 italic">Inga källor tillagda</div>
                                            </Show>
                                            <For each={agent.sources || []}>
                                                {(source) => {
                                                    const [localName, setLocalName] = createSignal(source.name)
                                                    const [localUrl, setLocalUrl] = createSignal(source.url)
                                                    
                                                    return (
                                                        <div class="mb-1 flex gap-1">
                                                            <input
                                                                type="text"
                                                                value={localName()}
                                                                maxLength={20}
                                                                placeholder="Källnamn"
                                                                onInput={(e) => setLocalName(e.currentTarget.value)}
                                                                onBlur={(e) => {
                                                                    scenarioActions.updateSource(agent.id, source.id, {
                                                                        name: e.currentTarget.value,
                                                                    })
                                                                    scenarioActions.scheduleSave()
                                                                }}
                                                                class="flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={localUrl()}
                                                                maxLength={100}
                                                                placeholder="URL"
                                                                onInput={(e) => setLocalUrl(e.currentTarget.value)}
                                                                onBlur={(e) => {
                                                                    scenarioActions.updateSource(agent.id, source.id, {
                                                                        url: e.currentTarget.value,
                                                                    })
                                                                    scenarioActions.scheduleSave()
                                                                }}
                                                                class="flex-[2] rounded border border-gray-300 px-2 py-1 text-xs"
                                                            />
                                                            <button
                                                                class="px-1 text-xs text-red-400 hover:text-red-600"
                                                                onClick={() => {
                                                                    scenarioActions.removeSource(agent.id, source.id)
                                                                    scenarioActions.scheduleSave()
                                                                }}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )
                                                }}
                                            </For>
                                        </div>

                                        <button
                                            class="w-full rounded border border-red-300 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                                            onClick={() => handleRemove(agent.id)}
                                        >
                                            Ta bort aktör
                                        </button>
                                    </div>
                                </Show>
                            </div>
                        )
                    }}
                </For>

                <Show when={scenario.agents.length === 0}>
                    <div class="p-4 text-center text-sm text-gray-400">Inga aktörer ännu.</div>
                </Show>
            </div>
        </>
    )
}

export default AgentPanel
